import React, { useEffect, useRef, useState } from 'react'
import { withStyles } from '@material-ui/core/styles'
import { createPortal } from 'react-dom'
import WaitScreen from '@minicoursegenerator/wait-screen'
import {
  WAIT_SCREEN_GRAY_THEME_CLASS,
  WAIT_SCREEN_GRAY_THEME_OVERRIDES,
  PROGRESS_BAR_STYLES,
  useBodyScrollLock,
  useWaitScreenStyles,
} from 'src/components/wait-screen'

const WAIT_SCREEN_SLIDES = [
  {
    title: 'Adding engaging interactions. ⚡',
    description: "We're building your course. It's almost ready.",
  },
  {
    title: 'Embedding community boards. 🤝',
    description: "We're building your course. It's almost ready.",
  },
  {
    title: 'Creating game challenges. 🎮',
    description: "We're building your course. It's almost ready.",
  },
  {
    title: 'Drafting quiz questions. 📝',
    description: "We're building your course. It's almost ready.",
  },
]

const NAVBAR_HEIGHT_PX = 64

const AiLoading = ({ progress = 0 }) => {
  const progressValue = Math.max(0, Math.min(100, progress))
  const progressLabel = `${Math.round(progressValue)}%`
  const waitScreenRef = useRef(null)
  const progressHostRef = useRef(null)
  const [progressHost, setProgressHost] = useState(null)

  // Ensure global styles are applied (lazy-loaded, only once)
  useWaitScreenStyles()
  // Use shared body scroll lock hook
  useBodyScrollLock()

  useEffect(() => {
    const ensureProgressHost = () => {
      const wrapper = waitScreenRef.current
      if (!wrapper) return
      const cardContent = wrapper.querySelector('.wait-screen-card-content')
      const imageContainer = wrapper.querySelector('.wait-screen-image-container')
      if (!cardContent || !imageContainer) return

      let host = wrapper.querySelector('.embedded-progress-host')
      if (!host) {
        host = document.createElement('div')
        host.className = 'embedded-progress-host'
      }
      if (host.parentNode !== cardContent) {
        cardContent.insertBefore(host, imageContainer)
      }
      if (host.nextSibling !== imageContainer) {
        cardContent.insertBefore(host, imageContainer)
      }
      progressHostRef.current = host
      setProgressHost(prev => (prev === host ? prev : host))
    }

    const rafId = requestAnimationFrame(ensureProgressHost)
    const timeoutId = setTimeout(ensureProgressHost, 120)
    const intervalId = setInterval(ensureProgressHost, 300)
    const observer = new MutationObserver(ensureProgressHost)
    if (waitScreenRef.current) {
      observer.observe(waitScreenRef.current, {
        childList: true,
        subtree: true,
      })
    }

    return () => {
      cancelAnimationFrame(rafId)
      clearTimeout(timeoutId)
      clearInterval(intervalId)
      observer.disconnect()
      if (progressHostRef.current && progressHostRef.current.parentNode) {
        progressHostRef.current.parentNode.removeChild(progressHostRef.current)
      }
    }
  }, [])

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          maxWidth: '1280px',
          padding: '0 16px',
        }}
      >
        <div
          className={`embedded-wait-screen ${WAIT_SCREEN_GRAY_THEME_CLASS}`}
          ref={waitScreenRef}
          style={{
            width: '100%',
            position: 'relative',
          }}
        >
          {progressHost
            ? createPortal(
                <div className="embedded-progress-bar">
                  <div className="wait-screen-progress-container">
                    <div className="wait-screen-progress-row">
                      <div className="wait-screen-progress-track">
                        <div
                          className="wait-screen-progress-fill"
                          style={{ width: `${progressValue}%` }}
                        />
                      </div>
                      <span className="wait-screen-progress-percent">
                        {progressLabel}
                      </span>
                    </div>
                  </div>
                </div>,
                progressHost,
              )
            : null}
          <style>{WAIT_SCREEN_GRAY_THEME_OVERRIDES}</style>
          <style>{PROGRESS_BAR_STYLES}</style>
          <style>
            {`
              .embedded-wait-screen {
                --top-navbar-height: ${NAVBAR_HEIGHT_PX}px;
              }
              .embedded-wait-screen .wait-screen {
                min-height: calc(100vh - var(--top-navbar-height)) !important;
                padding: 0.5rem 2rem 1.5rem !important;
              }
              .embedded-wait-screen .wait-screen-card {
                min-height: clamp(40rem, calc(90vh - var(--top-navbar-height)), calc(90vh - var(--top-navbar-height))) !important;
                padding: 0.5rem 2rem 1.5rem !important;
              }
              .embedded-wait-screen .embedded-progress-host {
                width: 100%;
              }
              .embedded-wait-screen .embedded-progress-bar {
                width: 100%;
                max-width: 62rem;
                margin: 0 auto;
              }
              .embedded-wait-screen .wait-screen-card-content {
                gap: 0.25rem !important;
                justify-content: center !important;
              }
              .embedded-wait-screen .wait-screen-progress-container {
                margin-bottom: 0 !important;
              }
              .embedded-wait-screen .wait-screen-title-container {
                padding-top: 0 !important;
                padding-bottom: 0 !important;
              }
              .embedded-wait-screen .wait-screen-description {
                margin-top: 0.25rem !important;
              }
              .embedded-wait-screen .wait-screen-image-container {
                margin-top: 0 !important;
              }
              .embedded-wait-screen .quiz-animation__container {
                max-width: min(740px, 100%) !important;
              }
              .embedded-wait-screen .quiz-animation__card {
                padding: 1.5rem !important;
              }
              .embedded-wait-screen .quiz-animation__option-row {
                max-width: 690px !important;
              }
              .embedded-wait-screen .quiz-animation__option-skeleton {
                min-width: 0 !important;
              }
              .embedded-wait-screen .quiz-animation__option-text-wrap {
                min-width: 0 !important;
              }
              .embedded-wait-screen .course-page-section > .mx-auto {
                width: min(100%, 680px) !important;
              }
              .embedded-wait-screen .wait-screen-slide-container {
                overflow-x: hidden !important;
              }
              @media (max-width: 1200px) {
                .embedded-wait-screen .quiz-animation__container {
                  max-width: min(700px, 100%) !important;
                }
                .embedded-wait-screen .quiz-animation__option-row {
                  max-width: 650px !important;
                }
                .embedded-wait-screen .course-page-section > .mx-auto {
                  width: min(100%, 640px) !important;
                }
              }
              @media (max-width: 900px) {
                .embedded-wait-screen .quiz-animation__container {
                  max-width: min(640px, 100%) !important;
                }
                .embedded-wait-screen .quiz-animation__card {
                  padding: 1.25rem !important;
                }
                .embedded-wait-screen .quiz-animation__option-row {
                  max-width: 600px !important;
                }
                .embedded-wait-screen .course-page-section > .mx-auto {
                  width: min(100%, 580px) !important;
                }
              }
              .embedded-wait-screen .quiz-cursor {
                margin-top: 10px !important;
              }
            `}
          </style>
          <WaitScreen slides={WAIT_SCREEN_SLIDES} />
        </div>
      </div>
    </div>
  )
}

export default withStyles({})(AiLoading)
